import { ConfigStateService, PermissionService } from '@abp/ng.core';
import {
  EnvironmentProviders,
  Injectable,
  inject,
  makeEnvironmentProviders,
  provideAppInitializer,
} from '@angular/core';
import { BehaviorSubject, of } from 'rxjs';
import { catchError, distinctUntilChanged, map } from 'rxjs/operators';
import { FacultyApproverService } from '../proxy/faculty/faculty-approver/faculty-approver.service';

@Injectable({ providedIn: 'root' })
export class FacultyManagementPendingApprovalCountService {
  private readonly subject = new BehaviorSubject<number>(0);
  readonly count$ = this.subject.asObservable();

  private readonly configState = inject(ConfigStateService);
  private readonly permissionService = inject(PermissionService);
  private readonly facultyApproverService = inject(FacultyApproverService);
  private isAuthenticated = false;

  init(): void {
    this.configState
      .getOne$('currentUser')
      .pipe(
        map(user => user?.isAuthenticated === true),
        distinctUntilChanged()
      )
      .subscribe(isAuthenticated => {
        this.isAuthenticated = isAuthenticated;

        if (!isAuthenticated) {
          this.subject.next(0);
          return;
        }

        this.refresh();
      });
  }

  refresh(): void {
    const isAuthenticated =
      this.isAuthenticated || this.configState.getOne('currentUser')?.isAuthenticated === true;

    if (!isAuthenticated || !this.permissionService.getGrantedPolicy('SelfServePortal.FacultyManagement')) {
      this.subject.next(0);
      return;
    }

    this.facultyApproverService
      .getCurrentUserPendingApprovalCount()
      .pipe(catchError(error => {
        console.error('Failed to fetch pending approval count:', error);
        return of(0);
      }))
      .subscribe(count => {
        this.subject.next(typeof count === 'number' ? count : 0);
      });
  }
}

export function provideFacultyManagementPendingApprovalCount(): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideAppInitializer(() => {
      inject(FacultyManagementPendingApprovalCountService).init();
    }),
  ]);
}
